import { useMemo, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import AssetItem from './Components/AssetItem';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import type { DeviceStats } from 'types/utilization.types';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import {
  commonListOptions,
  loaderListOptions,
  utilizationSortOptions,
} from '../constants';
import { getUtilizationOptionLabel } from '../utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface Props {
  title: string;
  data: DeviceStats[];
  isLoaderType?: boolean;
  utilizationType: string;
}

const AssetWrapper = ({
  title,
  data,
  isLoaderType = false,
  utilizationType,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme ? _palette.dark : _palette.light;

  const isLoading = useAppSelector(
    (state) => state.utilizationReducer.loadingStates.isLoadingFleetAssets
  );

  const options = useMemo(() => {
    if (isLoaderType) {
      return loaderListOptions;
    }

    return commonListOptions;
  }, [isLoaderType]);

  const [selectedSource, setSelectedSource] = useState<string[]>([
    'Utilization',
  ]);
  const [selectedSort, setSelectedSort] = useState<string>('Most Utilization');

  const sortedData = useMemo(() => {
    return data
      .map((item) => {
        return {
          ...item,
          utilization: Number(item.avg_utilization_by_shift_hours) * 100,
          activeHours: Number(item.total_engine_active_minutes),
        };
      })
      .filter((item) => {
        return Number(item.activeHours) > 0;
      })
      .sort((a: any, b: any) => {
        if (selectedSort === 'Least Utilization') {
          return a.utilization - b.utilization;
        }
        return b.utilization - a.utilization;
      });
  }, [data, selectedSort]);

  return (
    <Grid item lg={6} md={12}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? _palette.dark.boxBackground
            : _palette.light.boxBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.borderGrey}`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              sx={{ fontSize: '13px', fontWeight: '600', paddingTop: '5px' }}
            >
              {title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '600',
                color: palette.textSecondary,
                paddingRight: '15px',
                paddingTop: '5px',
              }}
            >
              {t('utilization_show_label')}
            </Typography>
            <ProductionSelector
              options={options}
              selected={selectedSource}
              setSelected={setSelectedSource}
              minWidth="150px"
              multiple={true}
              getOptionLabel={(option) => getUtilizationOptionLabel(t, option)}
            />
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '600',
                color: palette.textSecondary,
                paddingLeft: '10px',
                paddingRight: '15px',
                paddingTop: '5px',
              }}
            >
              {t('sort_by_10')}
            </Typography>
            <ProductionSelector
              options={utilizationSortOptions}
              selected={selectedSort}
              setSelected={setSelectedSort}
              minWidth="150px"
              getOptionLabel={(option) => getUtilizationOptionLabel(t, option)}
            />
          </Box>
        </Box>
        <Box sx={{ paddingTop: '20px' }}>
          {isLoading ? (
            <MultiColorCircularLoader height="200px" />
          ) : (
            <>
              {sortedData.map((item: any, index: number) => (
                <div key={index}>
                  <AssetItem
                    data={item}
                    selectedSource={selectedSource}
                    utilizationType={utilizationType}
                    getSourceLabel={(source) =>
                      getUtilizationOptionLabel(t, source)
                    }
                  />
                </div>
              ))}
            </>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default AssetWrapper;
